import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext.jsx';
import { deleteIssue, fetchIssueById, issuePath, updateIssue } from '../lib/newsletter.js';
import NewsletterForm from '../components/newsletter/NewsletterForm.jsx';

export default function NewsletterEdit() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAdmin, loading } = useAuth();
  const [issue, setIssue] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Wait for the auth context before fetching: a draft is only readable by
  // an admin, and the request would 0-row for an anonymous client.
  useEffect(() => {
    if (loading || !isAdmin) return undefined;
    let cancelled = false;
    fetchIssueById(id)
      .then((row) => !cancelled && (row ? setIssue(row) : setError(t('newsletter.form.notFound'))))
      .catch((err) => !cancelled && setError(err.message ?? String(err)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, loading, isAdmin]);

  if (loading) {
    return (
      <div role="status" aria-live="polite" className="mx-auto max-w-3xl px-8 pb-20 pt-32 text-center text-ink-secondary">
        {t('common.loading')}
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-2xl px-8 pb-20 pt-32 text-center">
        <h1 className="font-serif text-3xl text-gold-light">{t('common.error')}</h1>
        <p className="mt-3 text-ink-secondary">{t('newsletter.form.adminOnly')}</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-8 pb-20 pt-32 text-center">
        <h1 className="font-serif text-3xl text-gold-light">{t('common.error')}</h1>
        <p className="mt-3 text-ink-secondary">{error}</p>
      </div>
    );
  }
  if (!issue) {
    return (
      <div role="status" aria-live="polite" className="mx-auto max-w-3xl px-8 pb-20 pt-32 text-center text-ink-secondary">
        {t('common.loading')}
      </div>
    );
  }

  async function handleSubmit(form) {
    setBusy(true);
    try {
      const saved = await updateIssue(issue.id, form);
      navigate(issuePath(saved));
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    setBusy(true);
    try {
      await deleteIssue(issue.id);
      navigate('/newsletter');
    } catch (err) {
      setError(err.message ?? String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-4xl px-6 pb-20 pt-28">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="section-label">{t('newsletter.form.editLabel')}</div>
          <h1 className="font-serif text-3xl font-semibold text-cream">{issue.title}</h1>
        </div>
        {confirmDelete ? (
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="text-ink-secondary">{t('newsletter.form.confirmDelete')}</span>
            <button type="button" className="btn-ghost text-sm" onClick={() => setConfirmDelete(false)} disabled={busy}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="rounded-md border border-red-500/50 bg-red-950/30 px-4 py-2 text-sm font-medium text-red-200 hover:border-red-400 hover:text-red-100"
              onClick={handleDelete}
              disabled={busy}
            >
              {busy ? t('common.loading') : t('common.delete')}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="rounded-md border border-red-500/40 px-4 py-2 text-sm font-medium text-red-200 hover:border-red-400 hover:text-red-100"
            onClick={() => setConfirmDelete(true)}
          >
            {t('common.delete')}
          </button>
        )}
      </header>

      <NewsletterForm
        initialValue={issue}
        submitLabel={t('newsletter.form.save')}
        onSubmit={handleSubmit}
        onCancel={() => navigate(issuePath(issue))}
        busy={busy}
      />
    </div>
  );
}
